import { ExpenseType } from '@cobliteam/api-lib/src/dashboard/totalCostsOfOwnership/Types';
import { expenseTypeToStringMap } from '@cobliteam/api-lib/src/dashboard/totalCostsOfOwnership/utils';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectExpenseTypeProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const MultiSelectExpenseType = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectExpenseTypeProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [filter, setFilter] = useState(selectedOptions || []);

  const expenseTypes = Object.values(ExpenseType);

  const hasSameElements = selectedOptions?.length === filter?.length && selectedOptions.every((value) => filter.find((d) => d === value));

  const onClick = useCallback(() => {
    setFilter(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (typeId: string) => {
    const isSelected = filter.find((d) => d === typeId);
    const updatedSelected = isSelected ? filter.filter((d) => d !== typeId) : [...filter, typeId];
    setFilter(updatedSelected);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setFilter([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(filter);
    handleClose();
  }, [filter, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={filter?.length === 0} onClick={() => setFilter([])}>
              {t('expense_type.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={expenseTypes}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: ExpenseType) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={filter.includes(item)} onCheckedChange={() => onChangeSelect(item)} />
                  <Label htmlFor={item}>{expenseTypeToStringMap[item]}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('expense_type.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('expense_type.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='receipt' />}>
        {t('expense_type.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectExpenseType;
